import { useEffect, useState } from "react";
import { api } from "../api";
import type { ProfilePage } from "../api/pages";
import type { ShortLink } from "../api/short-links";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { EmptyState } from "../components/ui/empty-state";
import { SectionHeader } from "../components/ui/section-header";
import { PageCard } from "../components/PageCard";
import { ShortLinksTab } from "../components/ShortLinksTab";
import { CreatePageModal } from "../components/CreatePageModal";
import { PlanLimitModal } from "../components/PlanLimitModal";
import { RenewalBanner } from "../components/RenewalBanner";
import { navigate } from "../lib/router";
import { Plus, Search, SlidersHorizontal, Link as LinkIcon, Link2, LogOut, MessageCircle } from "lucide-react";
import { useLanguage } from "../hooks/useLanguage";
import { buildWhatsappLink } from "../lib/whatsapp";

type TabType = "pages" | "short-links";

const PLAN_LIMITS: Record<string, number> = {
  free: 1,
  pro: 2,
  premium: 5,
};

export function PageDashboard() {
  const { language, t } = useLanguage();
  const [pages, setPages] = useState<ProfilePage[]>([]);
  const [shortLinks, setShortLinks] = useState<ShortLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState<TabType>("pages");
  const [showCreateShortLink, setShowCreateShortLink] = useState(false);
  const [showCreatePageModal, setShowCreatePageModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [currentUser, setCurrentUser] = useState<{
    plan_type?: string;
    subscription_status?: string;
  } | null>(null);

  useEffect(() => {
    let active = true;

    // Check for checkout success
    const urlParams = new URLSearchParams(window.location.search);
    const checkoutStatus = urlParams.get("checkout");
    const sessionId = urlParams.get("session_id");

    if (checkoutStatus === "success") {
      // Clear the query param
      window.history.replaceState({}, "", window.location.pathname);

      const verifyAndRefresh = async () => {
        if (sessionId) {
          try {
            await api.verifySession(sessionId);
          } catch (e) {
            console.error("Error verifying session:", e);
          }
        }

        // Start polling for plan update
        let attempts = 0;
        const maxAttempts = 10; // 20 seconds total
        const pollInterval = setInterval(async () => {
          if (attempts >= maxAttempts || !active) {
            clearInterval(pollInterval);
            return;
          }

          try {
            const userRes = await api.me();
            if (userRes.ok && userRes.data.plan_type && userRes.data.plan_type !== "free") {
              setCurrentUser(userRes.data);
              clearInterval(pollInterval);
            }
          } catch (e) {
            console.error("Error polling user data:", e);
          }
          attempts++;
        }, 2000);
      };

      verifyAndRefresh();
    }

    // Load pages, short links, and user info concurrently
    Promise.all([
      api.getMyPages(),
      api.getShortLinks(),
      api.me()
    ]).then(([pagesRes, linksRes, userRes]) => {
      if (!active) return;

      // Handle pages response
      if (!pagesRes.ok) {
        setError(pagesRes.error);
        if (pagesRes.error.toLowerCase().includes("autenticado")) {
          // Faz logout local antes de redirecionar para login
          api.logout().catch(() => {
            // Ignora erros do logout
          }).finally(() => {
            navigate("/login");
          });
        }
        setLoading(false);
        return;
      }

      // Handle short links response
      if (!linksRes.ok) {
        setError(linksRes.error);
        setLoading(false);
        return;
      }

      // Handle user response
      if (userRes.ok) {
        setCurrentUser(userRes.data);
      } else {
        // Se houver erro ao verificar usuário, faz logout local
        api.logout().catch(() => {
          // Ignora erros do logout
        }).finally(() => {
          navigate("/login");
        });
        setLoading(false);
        return;
      }

      setPages(pagesRes.data || []);
      setShortLinks(Array.isArray(linksRes.data) ? linksRes.data : []);
      setLoading(false);
    }).catch(err => {
      if (!active) return;
      // Se houver erro de rede, faz logout local
      api.logout().catch(() => {
        // Ignora erros do logout
      }).finally(() => {
        setError("Erro ao carregar dados");
        setLoading(false);
        navigate("/login");
      });
    });

    return () => {
      active = false;
    };
  }, []);

  const handleLogout = async () => {
    await api.logout();
    navigate("/login");
  };

  const handleCreatePageClick = () => {
    const planType = currentUser?.plan_type || "free";
    const limit = PLAN_LIMITS[planType] || 1;

    if (pages.length >= limit) {
      setShowLimitModal(true);
    } else {
      setShowCreatePageModal(true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white selection:bg-indigo-100 selection:text-indigo-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-neutral-900/10 via-white to-white -z-10" />
        <div className="absolute top-0 left-0 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 -z-10 mix-blend-overlay"></div>
        <div className="text-center space-y-4">
          <div className="animate-spin mx-auto w-12 h-12 border-4 border-slate-500 border-t-transparent rounded-full"></div>
          <p className="text-slate-600 font-medium">{t.dashboard.loading}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6 bg-white selection:bg-indigo-100 selection:text-indigo-900 relative overflow-hidden">

        <div className="absolute top-0 left-0 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 -z-10 mix-blend-overlay"></div>
        <div className="bg-white p-8 rounded-3xl shadow-xl shadow-slate-900/5 border-2 border-slate-100 max-w-md w-full">
          <div className="w-16 h-16 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <SlidersHorizontal className="w-8 h-8" />
          </div>
          <p className="text-2xl font-bold text-slate-900 mb-2">{t.dashboard.errorTitle}</p>
          <p className="text-slate-600 mb-6">{error}</p>
          <Button
            variant="ghost"
            className="w-full h-12 rounded-xl mt-6"
            onClick={() => navigate("/login")}
          >
            {t.dashboard.errorAction}
          </Button>
        </div>
      </div>
    );
  }

  const hasPages = pages.length > 0;
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredPages =
    normalizedSearch.length > 0
      ? pages.filter(page =>
        `${page.title ?? ""} ${page.description ?? ""} ${page.slug}`
          .toLowerCase()
          .includes(normalizedSearch)
      )
      : pages;
  const hasFilteredResults = filteredPages.length > 0;

  const handlePageCreated = (pageSlug: string) => {
    // Reload pages to show the new one
    api.getMyPages().then(res => {
      if (res.ok) {
        setPages(res.data || []);
      }
    });
    // Navigate to edit the new page
    navigate(`/app/pagina/${pageSlug}`);
  };

  return (
    <div className="min-h-screen bg-white selection:bg-indigo-100 selection:text-indigo-900 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-neutral-900/10 via-white to-white -z-10" />
      <div className="absolute top-0 left-0 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 -z-10 mix-blend-overlay"></div>

      <div className="max-w-7xl mx-auto space-y-8">
        <RenewalBanner
          status={currentUser?.subscription_status}
          planType={currentUser?.plan_type}
        />

        <SectionHeader
          label="Hub Links"
          title={t.dashboard.title}
          subtitle={t.dashboard.subtitle}
          action={
            <div className="flex items-center gap-3">
              <div className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${(currentUser?.plan_type === 'premium')
                ? 'bg-gradient-to-r from-amber-200 to-yellow-400 text-yellow-900'
                : (currentUser?.plan_type === 'pro')
                  ? 'bg-gradient-to-r from-sky-200 to-blue-400 text-blue-900'
                  : 'bg-slate-100 text-slate-600'
                }`}>
                {currentUser?.plan_type === 'free' ? t.dashboard.planFree : currentUser?.plan_type || t.dashboard.planFree}
              </div>
              <Button
                variant="ghost"
                onClick={() => window.open(buildWhatsappLink("5551991543108", t.dashboard.whatsappMessage), '_blank')}
                className="text-slate-500 hover:text-green-600 hover:bg-green-50"
                icon={<MessageCircle className="w-4 h-4" />}
              >
                {t.dashboard.whatsappSupport}
              </Button>
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="text-slate-500 hover:text-red-600 hover:bg-red-50"
                icon={<LogOut className="w-4 h-4" />}
              >
                {t.dashboard.logout}
              </Button>
            </div>
          }
        />

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            variant="primary"
            onClick={handleCreatePageClick}
            icon={<Plus className="w-4 h-4" />}
          >
            {t.dashboard.createPage}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setActiveTab("short-links");
              setShowCreateShortLink(true);
            }}
            icon={<Plus className="w-4 h-4" />}
          >
            {t.dashboard.addShortLink}
          </Button>
        </div>

        {/* Global Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Total Accesses */}
          <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-slate-600">{t.dashboard.metrics.totalAccesses}</p>
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
            <p className="text-3xl font-bold text-slate-900">
              {(pages.reduce((sum, page) => sum + (page.today_accesses || 0), 0) +
                shortLinks.reduce((sum, link) => sum + (link.clicks || 0), 0)).toLocaleString()}
            </p>
          </div>

          {/* Page Accesses */}
          <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-slate-600">{t.dashboard.metrics.pageAccesses}</p>
              <div className="w-10 h-10 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl flex items-center justify-center">
                <LinkIcon className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <p className="text-3xl font-bold text-slate-900">
              {pages.reduce((sum, page) => sum + (page.today_accesses || 0), 0).toLocaleString()}
            </p>
          </div>

          {/* Short Link Clicks */}
          <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-slate-600">{t.dashboard.metrics.shortLinkClicks}</p>
              <div className="w-10 h-10 bg-gradient-to-br from-green-50 to-green-100 rounded-xl flex items-center justify-center">
                <Link2 className="w-5 h-5 text-green-600" />
              </div>
            </div>
            <p className="text-3xl font-bold text-slate-900">
              {shortLinks.reduce((sum, link) => sum + (link.clicks || 0), 0).toLocaleString()}
            </p>
          </div>
        </div>

        {/* Segmented Control */}
        <div className="flex rounded-xl bg-slate-100 p-1">
          <Button
            type="button"
            variant="tab"
            active={activeTab === "pages"}
            onClick={() => setActiveTab("pages")}
            className="font-semibold"
          >
            {t.dashboard.tabs.pages}
          </Button>
          <Button
            type="button"
            variant="tab"
            active={activeTab === "short-links"}
            onClick={() => setActiveTab("short-links")}
            className="font-semibold"
          >
            {t.dashboard.tabs.shortLinks}
          </Button>
        </div>

        {activeTab === "pages" ? (
          <>
            {hasPages ? (
              <div className="space-y-6">
                <div className="relative">
                  <label htmlFor="search-pages" className="sr-only">
                    Buscar páginas
                  </label>
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-slate-400" />
                  </div>
                  <input
                    id="search-pages"
                    type="text"
                    value={searchTerm}
                    onChange={event => setSearchTerm(event.target.value)}
                    placeholder={t.dashboard.search.placeholder}
                    className="block w-full pl-11 pr-4 py-3 border-2 border-slate-200 rounded-xl leading-5 bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500 sm:text-sm transition-all shadow-sm hover:shadow-md"
                  />
                </div>

                {hasFilteredResults ? (
                  <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {filteredPages.map(page => (
                      <PageCard
                        key={page.id}
                        page={page}
                        onEdit={() => navigate(`/app/pagina/${page.slug}`)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-3xl border-2 border-slate-100 p-12 text-center shadow-sm">
                    <div className="mx-auto w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mb-4">
                      <Search className="w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">{t.dashboard.search.noResults}</h3>
                    <p className="mt-2 text-slate-600">
                      {t.dashboard.search.noResultsDesc.replace("{searchTerm}", searchTerm)}
                    </p>
                    <Button
                      variant="ghost"
                      onClick={() => setSearchTerm("")}
                      className="mt-6"
                    >
                      {t.dashboard.search.clearSearch}
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <EmptyState
                icon={
                  <div className="mx-auto w-20 h-20 bg-gradient-to-br from-slate-100 to-slate-200 rounded-3xl flex items-center justify-center shadow-inner">
                    <LinkIcon className="w-10 h-10 text-slate-600" />
                  </div>
                }
                title={t.dashboard.emptyState.title}
                description={t.dashboard.emptyState.description}
                action={
                  <Button
                    variant="primary"
                    onClick={handleCreatePageClick}
                    className="px-8 py-4 text-base"
                    icon={<Plus className="w-5 h-5" />}
                  >
                    {t.dashboard.emptyState.action}
                  </Button>
                }
              />
            )}
          </>
        ) : (
          <ShortLinksTab
            showCreateForm={showCreateShortLink}
            onCreateFormClose={() => setShowCreateShortLink(false)}
            shortLinks={shortLinks}
            onShortLinksChange={setShortLinks}
          />
        )}

        {/* Create Page Modal */}
        <CreatePageModal
          open={showCreatePageModal}
          onClose={() => setShowCreatePageModal(false)}
          onSuccess={handlePageCreated}
        />

        {/* Plan Limit Modal */}
        <PlanLimitModal
          open={showLimitModal}
          onClose={() => setShowLimitModal(false)}
          currentPlan={currentUser?.plan_type || "free"}
          pageCount={pages.length}
          limit={PLAN_LIMITS[currentUser?.plan_type || "free"] || 1}
        />
      </div>
    </div>
  );
}
